/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: coaches' private notes for the workouts on screen. Notes live
 * apart from the cards and only come through the notes callables.
 */
import { useCallback, useEffect, useState } from "react";
import { getWorkoutNotes, saveWorkoutNote } from "@lemonadetraining/web-shared";
import { WORKOUT_NOTES_READ_CAP, type GetWorkoutNotesResponse } from "@lemonadetraining/contracts";

type Status = "idle" | "loading" | "ready" | "error";

export const useWorkoutNotes = (programmingUID: string, workoutIDs: string[], enabled: boolean) => {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [attempt, setAttempt] = useState(0);
  const key = [...workoutIDs].sort().join(",");

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return undefined;
    }
    // Nothing on screen yet: there is nothing to load, so notes can be written.
    if (workoutIDs.length === 0) {
      setStatus("ready");
      return undefined;
    }
    let live = true;
    setStatus("loading");
    const ids = key.split(",");
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += WORKOUT_NOTES_READ_CAP) {
      chunks.push(ids.slice(i, i + WORKOUT_NOTES_READ_CAP));
    }
    Promise.all(
      chunks.map(
        (chunk) => getWorkoutNotes(programmingUID, chunk) as Promise<GetWorkoutNotesResponse>
      )
    )
      .then((answers) => {
        if (!live) return;
        const next: Record<string, string> = {};
        answers.forEach((answer) =>
          Object.entries(answer?.notes ?? {}).forEach(([id, note]) => {
            next[id] = note.text;
          })
        );
        // The answer is the truth for these ids: a note cleared elsewhere disappears too.
        setNotes((prev) => {
          const merged = { ...prev };
          ids.forEach((id) => delete merged[id]);
          return { ...merged, ...next };
        });
        setStatus("ready");
      })
      .catch((error: unknown) => {
        console.error("Error loading private notes:", error);
        if (live) setStatus("error");
      });
    return () => {
      live = false;
    };
  }, [programmingUID, key, enabled, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  /** Resolves once saved; "" removes the note. */
  const save = useCallback(
    async (workoutID: string, text: string) => {
      const trimmed = text.trim();
      await saveWorkoutNote(programmingUID, workoutID, trimmed);
      setNotes((prev) => {
        const next = { ...prev };
        if (trimmed) next[workoutID] = trimmed;
        else delete next[workoutID];
        return next;
      });
    },
    [programmingUID]
  );

  return { notes, status, save, reload };
};
